import { useCallback, MouseEvent, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ContentCardButton,
  PlaceholderContent,
  ContentCardBox,
  ContentCardAnchorStyled,
} from 'approot/shared/content-card/content-card.styles';
import {
  WorksheetCard as WorksheetCardStyled,
  WorksheetCardTitle,
  WorksheetCardWrapper,
} from './worksheet-card.style';
import { Props, ContentLinkProps } from './worksheet-card.constants';
import LazyLoad from 'lib/lazyload';
import { isKeyboardModifierClick } from 'lib/browser';
import { clickLink } from 'approot/shared/content-card/content-card.utils';
import { useGenerateItemLink } from 'approot/shared/content-card/content-card.hooks';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import { InlineServerError } from 'approot/account/server-error';
import useUserData from 'approot/shared/user/user-data';
import { WorksheetCardImage } from './worksheet-card-image';
import ContentCardButtons from 'approot/shared/content-card-buttons/content-card-buttons';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { responsiveImageUrl } from '../images/imgix-responsive';
import { SimpleCardButton } from 'approot/shared/simple-card/simple-card.style';

const WorksheetCard = ({
  className,
  title,
  imageRatio,
  button,
  buttonSpace,
  dataEvent,
  dataEventAction,
  dataEventLabel,
  error,
  images,
  imageUrl,
  type,
  isAlwaysViewable = false,
  useLazyLoad = true,
  isCardButtonRow,
  canLink,
  hideLinkElement,
  isSelected,
  ariaLabel,
  onClick,
  ...props
}: Props & {
  onClick?: () => void;
}) => {
  const navigate = useNavigate();

  const { canViewPaidContent, canBookmarkContent } = useUserData();

  const canBookmark =
    !!props.isBookmarkable && !!props.allowBookmark && canBookmarkContent;

  const item: ContentLinkProps = {
    type: type,
    link: props.link,
    slug: props.slug,
    disabled: props.disabled,
  };
  const href = useGenerateItemLink({
    item,
    isAlwaysViewable,
    canOpenStimulus: canViewPaidContent,
  });

  const previewUrl = useMemo(
    () =>
      imageUrl
        ? imageUrl
        : responsiveImageUrl(images || [], 500, undefined, undefined),
    [imageUrl, images]
  );

  const handleLinkClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (dataEvent && dataEventAction && dataEventLabel) {
        pushDataTrackerEvent(dataEvent, dataEventAction, dataEventLabel);
      }
      if (isKeyboardModifierClick(e)) return;
      e.preventDefault();
      href && clickLink({ navigate, link: href });
    },
    [navigate, href, dataEvent, dataEventAction, dataEventLabel]
  );

  const cardButtons = (
    <ContentCardButtons
      isSmall
      canBookmark={props.isBookmarkable && canBookmark}
      canLink={canLink}
      link={href || ''}
      onLinkClick={handleLinkClick}
      disableBookmark={props.isBookmarkable && props.disableBookmark}
      disableBookmarkTooltipText={
        props.isBookmarkable && props.disableBookmark
          ? props.disableBookmarkTooltipText
          : undefined
      }
      bookmarkData={
        props.isBookmarkable
          ? {
              bookmarked: props.bookmarked,
              bookmarkDataEvent: props.bookmarkDataEvent,
              bookmarkDataEventAction: props.bookmarkDataEventAction,
              bookmarkDataEventLabel: props.bookmarkDataEventLabel,
              onBookmarkClick: props.onBookmarkClick,
            }
          : undefined
      }
    />
  );

  const cardInner = (
    <>
      {previewUrl && (
        <WorksheetCardImage
          key={previewUrl}
          imageUrl={previewUrl}
          ratio={imageRatio}
        />
      )}
    </>
  );

  const Placeholder = () => (
    <PlaceholderContent>
      <div></div>
    </PlaceholderContent>
  );

  const card = (
    <>
      {href && !hideLinkElement && (
        <ContentCardAnchorStyled
          href={href}
          onClick={handleLinkClick}
          aria-label={ariaLabel}
          data-selector="worksheet"
        >
          <ScreenReaderOnly>{title}</ScreenReaderOnly>
        </ContentCardAnchorStyled>
      )}

      {onClick && (
        <SimpleCardButton
          type="button"
          aria-label={ariaLabel}
          onClick={onClick}
        >
          <ScreenReaderOnly>{title}</ScreenReaderOnly>
        </SimpleCardButton>
      )}

      <WorksheetCardStyled
        ratio={imageRatio}
        isSelected={isSelected}
        isDisabled={!href}
      >
        {useLazyLoad ? (
          <LazyLoad
            unmountIfInvisible={true}
            offset={1000}
            placeholder={<Placeholder />}
            height={0}
          >
            {cardInner}
          </LazyLoad>
        ) : (
          cardInner
        )}
      </WorksheetCardStyled>
      {title && (
        <WorksheetCardTitle aria-hidden data-selector="card-title">
          {title}
        </WorksheetCardTitle>
      )}
      {cardButtons}
    </>
  );

  // Card is not a link in edit mode
  return (
    <WorksheetCardWrapper className={className} buttonSpace={buttonSpace}>
      <ContentCardBox data-selector="content-card-box">{card}</ContentCardBox>

      {error && <InlineServerError />}
      {button && <ContentCardButton {...button} />}
    </WorksheetCardWrapper>
  );
};

export default WorksheetCard;
